<script setup>
import { useAppointmentStore } from "@/composables/store/user/useAppointmentStore";
import { useUserAuthStore } from "@/composables/store/user/useAuthStore";
import { useWorkOrderStore } from "@/composables/store/user/useWorkOrderStore";
import DesktopDashboard from './components/DesktopDashboard.vue';
import MobileDashboard from './components/mobile/MobileDashboard.vue';

definePage({
  meta: {
    layout: "default",
    requiresAuth: true,
  },
});

// Store instances
const authStore = useUserAuthStore();
const appointmentStore = useAppointmentStore();
const workOrderStore = useWorkOrderStore();

// Shared state
const isLoading = ref(true);
const userData = ref(null);
const error = ref(null);

// Loading states
const loading = reactive({
  header: true,
  stats: true,
  actions: true,
  carousel: true
});

// Dashboard data and methods
const dashboardData = reactive({
  stats: [],
  quickActions: [], // Will be populated from the store
  recentUpdates: [],
  featuredContent: []
});

// Quick actions configuration
const quickActionsConfig = [
  // Property Owner Resources
  {
    icon: 'tabler-book',
    title: 'Property Owner Manuals',
    subtitle: 'Access property manuals and guides',
    route: '/user/manuals',
    color: 'primary'
  },
  {
    icon: 'tabler-clipboard',
    title: 'Forms',
    subtitle: 'Access and submit property forms',
    route: '/user/forms',
    color: 'info'
  },
  {
    icon: 'tabler-alert-triangle',
    title: 'Emergencies',
    subtitle: 'Emergency contacts and procedures',
    route: '/user/emergencies',
    color: 'error'
  },
  {
    icon: 'tabler-users',
    title: 'Community Board',
    subtitle: 'Community announcements and updates',
    route: '/user/community',
    color: 'success'
  },
  {
    icon: 'tabler-phone',
    title: 'Contact Numbers',
    subtitle: 'Important contact information',
    route: '/user/contacts',
    color: 'warning'
  },
  {
    icon: 'tabler-building-estate',
    title: 'Guard House',
    subtitle: 'Security and access information',
    route: '/user/guard-house',
    color: 'info'
  },
  {
    icon: 'tabler-tool',
    title: 'Support Services',
    subtitle: 'Maintenance and support services',
    route: '/user/support',
    color: 'primary'
  },
  {
    icon: 'tabler-link',
    title: 'Links',
    subtitle: 'Useful property-related links',
    route: '/user/links',
    color: 'success'
  },
  {
    icon: 'tabler-calendar',
    title: 'Appointments',
    subtitle: 'Schedule and manage appointments',
    route: '/user/appointments',
    color: 'info'
  },
  {
    icon: 'tabler-clipboard-list',
    title: 'Work Orders',
    subtitle: 'Submit and track work orders',
    route: '/user/work-orders',
    color: 'warning'
  },
  {
    icon: 'tabler-book',
    title: 'Bookings',
    subtitle: 'Book property facilities',
    route: '/user/bookings',
    color: 'primary'
  }
];

// Compute functions
const computeStats = (appointments = [], workOrders = []) => {
  // Ensure we have arrays
  // const appts = Array.isArray(appointments) ? appointments: [];
   // const wos = Array.isArray(workOrders) ? workOrders : [];
  const appts = Array.isArray(appointments.data.data) ? appointments.data.data : [];
  const apptsActive = appointments.status_counts.active || 0;
  const wosPending = workOrders.status_counts.active || 0;

  return [
    {
      label: 'Active Appointments',
      // value: appts.filter(apt => apt?.date && new Date(apt.date) >= new Date()).length || 0,
      value: apptsActive,
      icon: 'tabler-calendar-check',
      color: 'primary'
    },
    {
      label: 'Pending Work Orders',
      // value: wos.filter(wo => wo?.status === 'pending').length || 0,
      value: wosPending,
      icon: 'tabler-tool',
      color: 'warning'
    },
    {
      label: 'Upcoming Events',
      value: appts.filter(apt =>
        apt?.date &&
        new Date(apt.date) >= new Date() &&
        new Date(apt.date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      ).length || 0,
      icon: 'tabler-calendar-event',
      color: 'info'
    },
    {
      label: 'New Announcements',
      value: 0, // To be implemented with announcements store
      icon: 'tabler-speakerphone',
      color: 'error'
    }
  ];
};

const computeQuickActions = () => {
  return {
    categories: [
      {
        title: 'Appointments',
        icon: 'tabler-calendar',
        actions: [
          {
            icon: 'tabler-calendar-plus',
            title: 'Book Appointment',
            subtitle: 'Schedule a new appointment',
            route: '/user/appointments/create',
            color: 'primary'
          },
          {
            icon: 'tabler-calendar',
            title: 'My Appointments',
            subtitle: 'View your appointments',
            route: '/user/appointments',
            color: 'info'
          }
        ]
      },
      {
        title: 'Property Resources',
        icon: 'tabler-book',
        actions: [
          {
            icon: 'tabler-book',
            title: 'Property Owner Manuals',
            subtitle: 'Access property manuals and guides',
            route: '/user/manuals',
            color: 'primary'
          },
          {
            icon: 'tabler-clipboard',
            title: 'Forms',
            subtitle: 'Access and submit property forms',
            route: '/user/forms',
            color: 'info'
          }
        ]
      },
      {
        title: 'Facilities',
        icon: 'tabler-building',
        actions: [
          {
            icon: 'tabler-building-estate',
            title: 'Guard House',
            subtitle: 'Security and access information',
            route: '/user/guard-house',
            color: 'warning'
          },
          {
            icon: 'tabler-book',
            title: 'Facility Bookings',
            subtitle: 'Book property facilities',
            route: '/user/bookings',
            color: 'primary'
          }
        ]
      },
      {
        title: 'Support',
        icon: 'tabler-tool',
        actions: [
          {
            icon: 'tabler-alert-triangle',
            title: 'Emergencies',
            subtitle: 'Emergency contacts and procedures',
            route: '/user/emergencies',
            color: 'error'
          },
          {
            icon: 'tabler-tool',
            title: 'Work Orders',
            subtitle: 'Submit and track work orders',
            route: '/user/work-orders',
            color: 'warning'
          },
          {
            icon: 'tabler-phone',
            title: 'Contact Numbers',
            subtitle: 'Important contact information',
            route: '/user/contacts',
            color: 'info'
          }
        ]
      },
      {
        title: 'Community',
        icon: 'tabler-users',
        actions: [
          {
            icon: 'tabler-users',
            title: 'Community Board',
            subtitle: 'Community announcements and updates',
            route: '/user/community',
            color: 'success'
          },
          {
            icon: 'tabler-link',
            title: 'Useful Links',
            subtitle: 'Property-related resources',
            route: '/user/links',
            color: 'primary'
          }
        ]
      }
    ]
  };
};

// Initialize dashboard
const init = async () => {
  try {
    Object.keys(loading).forEach(key => loading[key] = true);

    // Fetch user data
    if (!authStore.user) {
      userData.value = await authStore.getAuthenticatedUser();
    } else {
      userData.value = authStore.user;
    }
    loading.header = false;

    // Fetch dashboard data
    const [appointmentsResponse, workOrdersResponse] = await Promise.all([
      appointmentStore.fetchAppointments({
        per_page: 5,
        sort_by: 'created_at',
        sort_order: 'desc'
      }),
      workOrderStore.fetchAllWorkOrders(),
    ]);

    // Extract appointments array from response
    // const appointments = appointmentsResponse?.data || [];
    // const workOrders = workOrdersResponse?.data || [];
    const appointments = appointmentsResponse || [];
    const workOrders = workOrdersResponse || [];

    console.log('Dashboard data:', {
      appointments,
      workOrders,
      appointmentsResponse,
      workOrdersResponse
    });

    // Update dashboard data
    dashboardData.stats = computeStats(appointments, workOrders);
    dashboardData.quickActions = computeQuickActions();

    // Add some sample data for recent updates and featured content
    dashboardData.recentUpdates = Array(4).fill(null).map((_, i) => ({
      id: i,
      title: `Update Title ${i + 1}`,
      description: 'Brief description of the update',
      date: new Date().toLocaleDateString()
    }));

    dashboardData.featuredContent = Array(3).fill(null).map((_, i) => ({
      id: i,
      title: `Featured Content ${i + 1}`,
      description: 'Description of featured content',
      image: `/images/featured-${i + 1}.jpg`
    }));

    loading.stats = false;
    loading.actions = false;
    setTimeout(() => loading.carousel = false, 800);

  } catch (err) {
    console.error('Dashboard initialization error:', err);
    error.value = 'Failed to load dashboard data';
  } finally {
    isLoading.value = false;
  }
};

// Lifecycle hooks
onMounted(() => {
  init();
});

// Pass props to components
const dashboardProps = computed(() => ({
  loading,
  error: error.value,
  userData: userData.value,
  dashboardData,
  onRefresh: init,
}));
</script>

<template>
  <div>
    <div class="md:hidden">
      <MobileDashboard v-bind="dashboardProps" />
    </div>
    <div class="hidden md:block">
      <DesktopDashboard v-bind="dashboardProps" />
    </div>
  </div>
</template>
